import { useCallback, useEffect, useRef, useState } from 'react';

import { descartarRascunho, obterRascunho, salvarRascunho } from '../api/rascunhosApi';
import { notify } from '../shared/feedback/notify';
import { EVENTO_ANTES_DE_RECARREGAR } from '../serviceWorkerRegistration';

/** Pausa de digitação até gravar. Curta o bastante para o reload da versão nova não pegar nada. */
export const ESPERA_PARA_GRAVAR_MS = 1500;

export type EstadoDoRascunho = 'inativo' | 'carregando' | 'pronto' | 'erro';

export interface UsoDoRascunho<T> {
  /** `pronto` só depois de a leitura RESPONDER — antes disso o formulário não grava por cima. */
  estado: EstadoDoRascunho;
  /** O rascunho encontrado ao abrir (`null` = não havia). */
  inicial: T | null;
  atualizadoEm: string | null;
  /** Agenda a gravação do conteúdo (com debounce). Ignorado enquanto não está `pronto`. */
  agendar: (conteudo: T) => void;
  /** Cancela o que estava agendado e apaga o rascunho no servidor. */
  descartar: () => Promise<void>;
}

/**
 * Rascunho de um formulário (oficina/at-136): carrega o que estava guardado ao ativar, grava com
 * debounce enquanto a pessoa preenche e grava NA HORA quando a versão nova do sistema vai
 * recarregar a página.
 *
 * 🔴 Três regras que vêm da at-60:
 * - falha ao LER nunca apaga o rascunho do servidor — avisa e deixa o formulário vazio;
 * - nada é gravado antes de a leitura responder, senão o formulário vazio da abertura
 *   sobrescreveria o rascunho que ainda estava chegando;
 * - falha ao GRAVAR avisa uma vez e não trava o preenchimento.
 */
export function useRascunho<T extends Record<string, unknown>>(chave: string | null, ativo: boolean): UsoDoRascunho<T> {
  const [estado, setEstado] = useState<EstadoDoRascunho>('inativo');
  const [inicial, setInicial] = useState<T | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null);

  const pendenteRef = useRef<T | null>(null);
  const temporizadorRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chaveRef = useRef(chave);
  const prontoRef = useRef(false);
  chaveRef.current = chave;

  const gravarPendente = useCallback(async () => {
    if (temporizadorRef.current) {
      clearTimeout(temporizadorRef.current);
      temporizadorRef.current = null;
    }
    const conteudo = pendenteRef.current;
    const alvo = chaveRef.current;
    if (!conteudo || !alvo) return;
    pendenteRef.current = null;
    try {
      await salvarRascunho(alvo, conteudo);
    } catch {
      notify.warning(
        'Não foi possível guardar o rascunho deste formulário. Você pode continuar preenchendo; se a página recarregar, o que não foi salvo se perde.',
        { toastId: 'rascunho-falha-ao-gravar' },
      );
    }
  }, []);

  useEffect(() => {
    prontoRef.current = false;
    pendenteRef.current = null;
    setInicial(null);
    setAtualizadoEm(null);
    if (!ativo || !chave) {
      setEstado('inativo');
      return;
    }
    let vivo = true;
    setEstado('carregando');
    obterRascunho<T>(chave)
      .then((r) => {
        if (!vivo) return;
        setInicial(r?.conteudo ?? null);
        setAtualizadoEm(r?.atualizadoEm ?? null);
        prontoRef.current = true;
        setEstado('pronto');
      })
      .catch(() => {
        if (!vivo) return;
        notify.warning(
          'Não foi possível verificar se havia um rascunho deste formulário. Ele abriu vazio; o rascunho guardado, se houver, não foi apagado.',
          { toastId: 'rascunho-falha-ao-ler' },
        );
        setEstado('erro');
      });
    return () => {
      vivo = false;
      // Fechar o formulário não pode perder a última digitação.
      void gravarPendente();
    };
  }, [chave, ativo, gravarPendente]);

  // Antes do reload da versão nova, grava o que estiver pendente — e o reload espera.
  useEffect(() => {
    if (!ativo || !chave) return undefined;
    const ouvinte = (e: Event) => {
      const aguardar = (e as CustomEvent).detail?.aguardar as ((p: Promise<unknown>) => void) | undefined;
      const gravacao = gravarPendente();
      aguardar?.(gravacao);
    };
    window.addEventListener(EVENTO_ANTES_DE_RECARREGAR, ouvinte);
    return () => window.removeEventListener(EVENTO_ANTES_DE_RECARREGAR, ouvinte);
  }, [ativo, chave, gravarPendente]);

  const agendar = useCallback(
    (conteudo: T) => {
      // Depois de uma falha de leitura também grava: é a sessão atual que vale a partir daí.
      if (!prontoRef.current && estado !== 'erro') return;
      pendenteRef.current = conteudo;
      if (temporizadorRef.current) clearTimeout(temporizadorRef.current);
      temporizadorRef.current = setTimeout(() => void gravarPendente(), ESPERA_PARA_GRAVAR_MS);
    },
    [estado, gravarPendente],
  );

  const descartar = useCallback(async () => {
    if (temporizadorRef.current) clearTimeout(temporizadorRef.current);
    temporizadorRef.current = null;
    pendenteRef.current = null;
    setInicial(null);
    setAtualizadoEm(null);
    const alvo = chaveRef.current;
    if (!alvo) return;
    try {
      await descartarRascunho(alvo);
    } catch {
      notify.warning('Não foi possível apagar o rascunho deste formulário. Ele pode ser oferecido de novo na próxima abertura.', {
        toastId: 'rascunho-falha-ao-descartar',
      });
    }
  }, []);

  return { estado, inicial, atualizadoEm, agendar, descartar };
}
